import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./employees-list.scss";
import axiosInstance from "../../../httpInterceptor";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  TextField,
  IconButton,
  Pagination,
  Box,
  styled,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { FaSort } from "react-icons/fa6";
import SearchIcon from "@mui/icons-material/Search";
import { MdOutlineKeyboardArrowLeft } from "react-icons/md";
import { MdOutlineKeyboardArrowRight } from "react-icons/md";
// import Pagination from "../../layout/pagination/Pagination";
interface EmployeeTableProps {
  onRowClick: (employee: Employee) => void;
  updatedEmployee?: Employee | null;
}

export interface Employee {
  id: number;
  name: string;
  email: string;
  role: string;
  psNo: string;
  timeStamp: string;
}

const EmployeeTable: React.FC<EmployeeTableProps> = ({ onRowClick, updatedEmployee }) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [query, setQuery] = useState({
    name: "",
    psNo: "",
    email: "",
    role: "",
  });
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<keyof Employee>("name");
  const [recentlyUpdatedPsNos, setRecentlyUpdatedPsNos] = useState<string[]>(() => {
    // Load array from localStorage on mount
    const stored = localStorage.getItem('recentlyUpdatedPsNos');
    return stored ? JSON.parse(stored) : [];
  });

  const isSmallScreen = useMediaQuery('(max-width:1200px)');
  useEffect(() => {
    axiosInstance
      .get(`${process.env.REACT_APP_USER_SERVICE_BASE_URL}/userdata`)
      .then((response) => {
        let reversedData = response.data.reverse();
        
        // If there are recently updated employees, move them to the top in order
        if (recentlyUpdatedPsNos.length > 0) {
          const recentlyUpdated: Employee[] = [];
          const others: Employee[] = [];
          
          // Separate recently updated from others
          reversedData.forEach((emp: Employee) => {
            if (recentlyUpdatedPsNos.includes(emp.psNo)) {
              recentlyUpdated.push(emp);
            } else {
              others.push(emp);
            }
          });
          
          // Sort recently updated by their order in the array (most recent first)
          recentlyUpdated.sort((a, b) => {
            return recentlyUpdatedPsNos.indexOf(a.psNo) - recentlyUpdatedPsNos.indexOf(b.psNo);
          });
          
          reversedData = [...recentlyUpdated, ...others];
        }
        
        setEmployees(reversedData);
        setFilteredEmployees(reversedData);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("There was an error fetching the employees!", error);
        setIsLoading(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Update employee and move to top when role is changed
  useEffect(() => {
    if (updatedEmployee) {
      // Add to the beginning of the update history array
      setRecentlyUpdatedPsNos((prev) => {
        // Remove if already exists, then add to front
        const filtered = prev.filter(psNo => psNo !== updatedEmployee.psNo);
        const newArray = [updatedEmployee.psNo, ...filtered];
        // Persist to localStorage
        localStorage.setItem('recentlyUpdatedPsNos', JSON.stringify(newArray));
        return newArray;
      });
      
      setEmployees((prevEmployees) => {
        // Find and update the employee
        const updatedList = prevEmployees.map((emp) => 
          emp.psNo === updatedEmployee.psNo ? { ...emp, role: updatedEmployee.role, timeStamp: updatedEmployee.timeStamp } : emp
        );
        
        // Move the updated employee to the top
        const updatedEmpIndex = updatedList.findIndex(emp => emp.psNo === updatedEmployee.psNo);
        if (updatedEmpIndex !== -1) {
          const updatedEmp = updatedList.splice(updatedEmpIndex, 1)[0];
          return [updatedEmp, ...updatedList];
        }
        
        return updatedList;
      });
    }
  }, [updatedEmployee]);

  useEffect(() => {
    let filtered = employees;
    if (query.name) {
      filtered = filtered.filter((employee) =>
        employee.name.toLowerCase().includes(query.name.toLowerCase())
      );
    }
    if (query.psNo) {
      filtered = filtered.filter((employee) =>
        employee.psNo.toLowerCase().includes(query.psNo.toLowerCase())
      );
    }
    if (query.email) {
      filtered = filtered.filter((employee) =>
        employee.email.toLowerCase().includes(query.email.toLowerCase())
      );
    }
    if (query.role) {
      filtered = filtered.filter((employee) =>
        employee.role.toLowerCase().includes(query.role.toLowerCase())
      );
    }
    setFilteredEmployees(filtered);
  }, [query, employees]);

  const handleRowClick = (employee: Employee) => {
    onRowClick(employee);
  };

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery({
      ...query,
      [e.target.name]: e.target.value,
    });
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Employee
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    // Clear the recently updated priority when user manually sorts
    setRecentlyUpdatedPsNos([]);
    localStorage.removeItem('recentlyUpdatedPsNos');
  };
  const getSortIcon = (property: any) => {
    return <FaSort style={{ color: "black", transform: "translateX(7px)" }} />;
  };

  const sortedEmployees = [...filteredEmployees].sort((a, b) => {
    // Always keep recently updated employees at the top in order
    if (recentlyUpdatedPsNos.length > 0) {
      const aIndex = recentlyUpdatedPsNos.indexOf(a.psNo);
      const bIndex = recentlyUpdatedPsNos.indexOf(b.psNo);
      
      // Both are in the recently updated list - sort by their position in the array
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      // Only a is recently updated - it comes first
      if (aIndex !== -1) return -1;
      // Only b is recently updated - it comes first
      if (bIndex !== -1) return 1;
    }
    
    // Neither is recently updated, or no recently updated items - use normal sorting
    if (
      orderBy === "name" ||
      orderBy === "role" ||
      orderBy === "email" ||
      orderBy === "psNo" ||
      orderBy === "timeStamp"
    ) {
      return order === "asc"
        ? String(a[orderBy]).localeCompare(String(b[orderBy]))
        : String(b[orderBy]).localeCompare(String(a[orderBy]));
    } else {
      return order === "asc"
        ? (a[orderBy] as number) - (b[orderBy] as number)
        : (b[orderBy] as number) - (a[orderBy] as number);
    }
  });
  const SortIconButton = styled(IconButton)`
  visibility: hidden;
  &:hover {
    visibility: visible;
  }
`;

  const handleItemsPerPageChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when items per page changes
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedEmployees.slice(indexOfFirstItem, indexOfLastItem);
  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < Math.ceil(filteredEmployees.length / itemsPerPage)) {
      setCurrentPage(currentPage + 1);
    }
  };
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
  };

  return (
    <div className="projectlistTableContainer2" style={{ height: '100%', display: 'flex', flexDirection: 'column', overflowY: 'auto',translate:'0px -10px',overflowX:'hidden',scrollbarWidth:'thin' }}>
      <TableContainer
        component={Paper}
        style={{
          height: 'fit-content',
          maxHeight: 'calc(100vh - 150px)',
          width: '100%',
          overflow: 'visible',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e5e7eb',
          translate:'0px 0px',
          display: 'flex',
          flexDirection: 'column',
          scrollbarWidth:'thin'
        }}
      >
        <Table className="tstylee2" style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'separate', borderSpacing: 0 }}>
          <TableHead style={{ position: "sticky", top: "0", zIndex: "1", boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)' }}>
            <TableRow style={{ backgroundColor: "#D3D3D3", height: "auto" }}>
              <TableCell style={{
                fontSize: '0.8rem',
                color: "#1a1a1a",
                padding: "10px 16px",
                textAlign: "left",
                backgroundColor: "#D3D3D3",
                verticalAlign: "middle",
                fontWeight: "700",
                width: "20%",
                letterSpacing: '0.5px',
                borderBottom: '3px solid #b0b0b0',
                textTransform: 'uppercase'
              }}>
                <TableSortLabel
                  active={orderBy === "name"}
                  direction={orderBy === "name" ? order : "asc"}
                  onClick={(event) => handleRequestSort(event, "name")}
                  IconComponent={() => getSortIcon("name")}
                  sx={{ 
                    fontWeight: "700", 
                    fontSize: "0.8rem", 
                    '&:hover': { color: '#000', opacity: 0.8 },
                    '& .MuiTableSortLabel-icon': { opacity: 0.6 }
                  }}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell style={{
                fontSize: '0.8rem',
                color: "#1a1a1a",
                padding: "10px 16px",
                textAlign: "left",
                backgroundColor: "#D3D3D3",
                verticalAlign: "middle",
                fontWeight: "700",
                width: "15%",
                letterSpacing: '0.5px',
                borderBottom: '3px solid #b0b0b0',
                textTransform: 'uppercase'
              }}>
                <TableSortLabel
                  active={orderBy === "psNo"}
                  direction={orderBy === "psNo" ? order : "asc"}
                  onClick={(event) => handleRequestSort(event, "psNo")}
                  IconComponent={() => getSortIcon("psNo")}
                  sx={{ 
                    fontWeight: "700", 
                    fontSize: "0.8rem", 
                    '&:hover': { color: '#000', opacity: 0.8 },
                    '& .MuiTableSortLabel-icon': { opacity: 0.6 }
                  }}
                >
                  PS ID
                </TableSortLabel>
              </TableCell>
              <TableCell style={{
                fontSize: '0.8rem',
                color: "#1a1a1a",
                padding: "10px 16px",
                textAlign: "left",
                backgroundColor: "#D3D3D3",
                verticalAlign: "middle",
                fontWeight: "700",
                width: "35%",
                letterSpacing: '0.5px',
                borderBottom: '3px solid #b0b0b0',
                textTransform: 'uppercase'
              }}>
                <TableSortLabel
                  active={orderBy === "email"}
                  direction={orderBy === "email" ? order : "asc"}
                  onClick={(event) => handleRequestSort(event, "email")}
                  IconComponent={() => getSortIcon("email")}
                  sx={{
                    fontWeight: "700",
                    fontSize: "0.8rem",
                    '&:hover': { color: '#000', opacity: 0.8 },
                    '& .MuiTableSortLabel-icon': { opacity: 0.6 }
                  }}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell style={{
                fontSize: '0.8rem',
                color: "#1a1a1a",
                padding: "10px 16px",
                textAlign: "left",
                backgroundColor: "#D3D3D3",
                verticalAlign: "middle",
                fontWeight: "700",
                width: "30%",
                letterSpacing: '0.5px',
                borderBottom: '3px solid #b0b0b0',
                textTransform: 'uppercase'
              }}>
                <TableSortLabel
                  active={orderBy === "role"}
                  direction={orderBy === "role" ? order : "asc"}
                  onClick={(event) => handleRequestSort(event, "role")}
                  IconComponent={() => getSortIcon("role")}
                  sx={{ 
                    fontWeight: "700", 
                    fontSize: "0.8rem", 
                    '&:hover': { color: '#000', opacity: 0.8 },
                    '& .MuiTableSortLabel-icon': { opacity: 0.6 }
                  }}
                >
                  Role
                </TableSortLabel>
              </TableCell>
            </TableRow>

            {/* Search Input Row */}
            <TableRow style={{ height: "12px", borderBottom: "2px solid #d5d5d5", boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.03)' }}>
              <td className="inputContainer1" style={{ backgroundColor: "#F5F5F5", padding: "8px 16px" }}>
                <input
                  type="text"
                  name="name"
                  onChange={handleQueryChange}
                  value={query.name}
                  style={{ 
                    height: "36px", 
                    borderRadius: '8px', 
                    border: '1.5px solid #d5d5d5',
                    padding: '0 12px',
                    fontSize: '0.85rem',
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    width: '100%',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                    outline: 'none'
                  }}
                  placeholder="Search"
                  aria-label="Search name"
                  onFocus={(e) => {
                    e.target.style.outline = 'none';
                    e.target.style.borderColor = '#1a73e8';
                    e.target.style.boxShadow = '0 2px 8px rgba(26,115,232,0.12), 0 0 0 3px rgba(26,115,232,0.08)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d5d5d5';
                    e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                    e.target.style.outline = 'none';
                  }}
                />
                {query.name.length === 0 && (
                  <i className="fa-solid fa-magnifying-glass" style={{ color: '#1a73e8', fontSize: '0.95rem',translate:'-10px' }}></i>
                )}
              </td>
              <td className="inputContainer1" style={{ backgroundColor: "#F5F5F5", padding: "8px 16px" }}>
                <input
                  type="text"
                  name="psNo"
                  onChange={handleQueryChange}
                  value={query.psNo}
                  style={{ 
                    height: "32px", 
                    borderRadius: '8px', 
                    border: '1.5px solid #d5d5d5',
                    padding: '0 12px',
                    fontSize: '0.85rem',
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    width: '100%',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                    outline: 'none'
                  }}
                  placeholder="Search"
                  aria-label="Search PS ID"
                  onFocus={(e) => {
                    e.target.style.outline = 'none';
                    e.target.style.borderColor = '#1a73e8';
                    e.target.style.boxShadow = '0 2px 8px rgba(26,115,232,0.12), 0 0 0 3px rgba(26,115,232,0.08)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d5d5d5';
                    e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                    e.target.style.outline = 'none';
                  }}
                />
                {query.psNo.length === 0 && (
                  <i className="fa-solid fa-magnifying-glass" style={{ color: '#1a73e8', fontSize: '0.95rem',translate:'-10px' }}></i>
                )}
              </td>
              <td className="inputContainer1" style={{ backgroundColor: "#F5F5F5", padding: "8px 16px" }}>
                <input
                  type="text"
                  name="email"
                  onChange={handleQueryChange}
                  value={query.email}
                  style={{ 
                    height: "32px", 
                    borderRadius: '8px', 
                    border: '1.5px solid #d5d5d5',
                    padding: '0 12px',
                    fontSize: '0.85rem',
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    width: '100%',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                    outline: 'none'
                  }}
                  placeholder="Search"
                  aria-label="Search email"
                  onFocus={(e) => {
                    e.target.style.outline = 'none';
                    e.target.style.borderColor = '#1a73e8';
                    e.target.style.boxShadow = '0 2px 8px rgba(26,115,232,0.12), 0 0 0 3px rgba(26,115,232,0.08)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d5d5d5';
                    e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                    e.target.style.outline = 'none';
                  }}
                />
                {query.email.length === 0 && (
                  <i className="fa-solid fa-magnifying-glass" style={{ color: '#1a73e8', fontSize: '0.95rem',translate:'-10px' }}></i>
                )}
              </td>
              <td className="inputContainer1" style={{ backgroundColor: "#F5F5F5", padding: "8px 16px" }}>
                <input
                  type="text"
                  name="role"
                  onChange={handleQueryChange}
                  value={query.role}
                  style={{ 
                    height: "32px", 
                    borderRadius: '8px', 
                    border: '1.5px solid #d5d5d5',
                    padding: '0 12px',
                    fontSize: '0.85rem',
                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                    width: '100%',
                    backgroundColor: '#ffffff',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                    outline: 'none'
                  }}
                  placeholder="Search"
                  aria-label="Search role"
                  onFocus={(e) => {
                    e.target.style.outline = 'none';
                    e.target.style.borderColor = '#1a73e8';
                    e.target.style.boxShadow = '0 2px 8px rgba(26,115,232,0.12), 0 0 0 3px rgba(26,115,232,0.08)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#d5d5d5';
                    e.target.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.05)';
                    e.target.style.outline = 'none';
                  }}
                />
                {query.role.length === 0 && (
                  <i className="fa-solid fa-magnifying-glass" style={{ color: '#1a73e8', fontSize: '0.95rem',translate:'-10px' }}></i>
                )}
              </td>
            </TableRow>
          </TableHead>
          <TableBody style={{ backgroundColor: "#F5F5F5" }}>
            {currentItems.length > 0 ? (
              currentItems.map((employee, index) => (
                <tr
                  key={employee.psNo}
                  onClick={() => handleRowClick(employee)}
                  style={{
                    cursor: "pointer",
                    height: "45px",
                    borderBottom: "1px solid #e8e8e8",
                    backgroundColor: index % 2 === 0 ? "#F9F9F9" : "#ffe4e2"
                  }}
                >
                  <td style={{ 
                    padding: "10px 16px", 
                    textAlign: "left",
                    fontSize: '0.9rem',
                    color: '#313131',
                    fontWeight: '400',
                    letterSpacing: '0.2px'
                  }}>
                    {employee.name}
                  </td>
                  <td style={{ 
                    padding: "10px 16px", 
                    textAlign: "left",
                    fontSize: '0.85rem',
                    color: '#313131',
                    fontWeight: '400'
                  }}>
                    {employee.psNo}
                  </td>
                  <td style={{ 
                    padding: "10px 16px", 
                    textAlign: "left",
                    fontSize: '0.85rem',
                    color: '#313131',
                    fontWeight: '400'
                  }}>
                    {employee.email}
                  </td>
                  <td style={{ 
                    padding: "10px 16px", 
                    textAlign: "left"
                  }}>
                    <span style={{
                      backgroundColor: (() => {
                        const roleLower = employee.role?.toLowerCase() || '';
                        if (roleLower === 'admin') return '#004B88';
                        if (roleLower === 'project manager') return '#5A3A83';
                        if (roleLower === 'reviewer') return '#35754D';
                        return '#E54536'; // NA or any other role
                      })(),
                      color: '#ffffff',
                      padding: '4px 12px',
                      borderRadius: '10px',
                      fontSize: '0.85rem',
                      fontWeight: '500',
                      display: 'inline-block'
                    }}>
                      {employee.role}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={4}
                  style={{
                    textAlign: "center",
                    fontSize: "0.95rem",
                    color: "#074173",
                    backgroundColor: "#F5F5F5",
                    fontWeight: "600",
                    padding: "40px 20px",
                    letterSpacing: '0.5px'
                  }}
                >
                  {isLoading ? (
                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
                      <div style={{ marginRight: 8, fontWeight: 600, color: '#074173' }}>Access management data</div>
                       <CircularProgress size={28} />
                    </div>
                  ) : (
                    'No Record Found'
                  )}
                </td>
              </tr>
            )}
          </TableBody>
        </Table>

        {filteredEmployees.length > 0 && (
          <div
            className="pagination"
            style={{
              backgroundColor: "#EFEFEF",
              height: "3.5rem",
              minHeight: "3.5rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 1.5rem",
              fontFamily: "'Roboto', sans-serif",
              borderTop: "2px solid #d5d5d5",
              borderBottomLeftRadius: '16px',
              borderBottomRightRadius: '16px',
              boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.05)'
            }}
          >
            {/* Left section: Rows per page dropdown */}
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <label htmlFor="itemsPerPage" style={{ fontWeight: 400, color: '#5f6368', fontSize: '0.875rem' }}>
                Rows per page:
              </label>
              <select
                id="itemsPerPage"
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                style={{
                  padding: "0.25rem 1.5rem 0.25rem 0.5rem",

                  borderRadius: "4px",
                  border: "1px solid #dadce0",
                  backgroundColor: "white",
                  fontSize: "0.875rem",
                  height: "2rem",
                  color: '#202124',
                  cursor: "pointer",
                  appearance: "none",
                  backgroundImage: "url('data:image/svg+xml;charset=UTF-8,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2712%27 height=%278%27 viewBox=%270 0 12 8%27%3E%3Cpath fill=%27%23202124%27 d=%27M6 8L0 2h12z%27/%3E%3C/svg%3E')",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 0.5rem center",
                  width: '4rem'
                }}
              >
                {[5, 10, 25, 50, 75, 100].map((value) => (
                  <option
                    key={value}
                    value={value}
                  >
                    {value}
                  </option>
                ))}
              </select>
            </div>

            {/* Center section: Showing text and Page info */}
            <div style={{ display: "flex", alignItems: "center", gap: "2rem" }}>
              <span style={{ fontWeight: 400, color: '#5f6368', fontSize: '0.875rem' }}>
                Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filteredEmployees.length)} of {filteredEmployees.length} items
              </span>
              <span style={{ fontWeight: 400, color: '#5f6368', fontSize: '0.875rem', translate: '160px' }}>
                Page {currentPage} of {Math.ceil(filteredEmployees.length / itemsPerPage)}
              </span>
            </div>

            {/* Right section: Navigation buttons */}
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", translate: '-250px' }}>
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                style={{
                  backgroundColor: "transparent",
                  border: "none",
                  borderRadius: "4px",
                  padding: "0.5rem",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  opacity: currentPage === 1 ? 0.4 : 1,
                  fontSize: "1.5rem",
                  color: '#5f6368',
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <MdOutlineKeyboardArrowLeft />
              </button>

              <div style={{
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                backgroundColor: "#1a73e8",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
                fontSize: "0.875rem",
                fontWeight: 500,
                translate: '230px'
              }}>
                {/* {currentPage} */}
              </div>

              <button
                onClick={handleNextPage}
                disabled={
                  currentPage ===
                  Math.ceil(filteredEmployees.length / itemsPerPage)
                }
                style={{
                  backgroundColor: "transparent",
                  border: "none",
                  borderRadius: "4px",
                  padding: "0.5rem",
                  cursor:
                    currentPage ===
                      Math.ceil(filteredEmployees.length / itemsPerPage)
                      ? "not-allowed"
                      : "pointer",
                  opacity:
                    currentPage ===
                      Math.ceil(filteredEmployees.length / itemsPerPage)
                      ? 0.4
                      : 1,
                  fontSize: "1.5rem",
                  color: '#5f6368',
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}
              >
                <MdOutlineKeyboardArrowRight />
              </button>
            </div>
          </div>
        )}
      </TableContainer>
    </div>
  );
};

export default EmployeeTable;
